import { FinancialType, TrackingHelper } from '@/helpers/tracking.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatSlideToggle, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FinancialData, RealProperty, TrackingService } from '@eemo/api-client';
import { ChartType, GoogleChartComponent, GoogleChartsModule, ScriptLoaderService } from 'angular-google-charts';

@Component({
    selector: 'app-tracking-financial',
    templateUrl: './tracking-financial.component.html',
    styleUrls: ['./tracking-financial.component.scss'],
    standalone: true,
    imports: [NgIf, NgFor, MatSlideToggleModule, GoogleChartsModule]
})
export class TrackingFinancialComponent implements OnInit {
  @Input() prop: RealProperty;
  @ViewChild('chart') chart: GoogleChartComponent;
  @ViewChildren('financialType') financialTypeSliders: QueryList<MatSlideToggle>;

  chartLoaded = false;
  chartAxisTimeValues = [];

  chartOptions = {
    animation: {
      duration: 1000,
      easing: 'out'
    },
    legend: { position: 'bottom' },
    series: {},
    vAxes: {},
    pointSize: 5,
    height: 500
  };

  availableFinancialTypes: FinancialType[] = ['GLOBAL'];
  selectedFinancialTypes: FinancialType[] = ['GLOBAL'];
  financialData: FinancialData[];


  chartType = ChartType.ComboChart;
  columns: [];
  chartData: any = [];
  options = {
    animation: {
      duration: 1000,
      easing: 'out'
    },
    legend: {
      position: 'bottom'
    },
    pointSize: 5,
    height: 500,
    vAxes: {
      0: {
        title: ""
      },
      1: {
        title: ""
      }
    },
    series: {
      0: {
        targetAxisIndex: 0,
        type: 'bars'
      },
      1: {
        targetAxisIndex: 1,
        type: 'line',
      }
    }
  }

  constructor(
    private chartLoaderService: ScriptLoaderService,
    private trackingService: TrackingService,
    public trackingHelper: TrackingHelper,
    public userHelper: UserHelper,
    private ref: ChangeDetectorRef,
    private alertService: AlertService
  ) {}

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.trackingService.getPropertyFinancialReport(this.prop.uid).subscribe((data) => {
      this.financialData = data.sort(this.sortDataPerYear);
      data.forEach((item) => {
        if (item.energyCost && !this.availableFinancialTypes.includes('ENERGY')) {
          this.availableFinancialTypes.push('ENERGY');
        }
        if (item.maintenanceCost && !this.availableFinancialTypes.includes('MAINTENANCE')) {
          this.availableFinancialTypes.push('MAINTENANCE');
        }
        if (item.workCost && !this.availableFinancialTypes.includes('WORK')) {
          this.availableFinancialTypes.push('WORK');
        }
        if (item.chargeCost && !this.availableFinancialTypes.includes('CHARGE')) {
          this.availableFinancialTypes.push('CHARGE');
        }
      });
      this.availableFinancialTypes.sort(this.sortFinancialType);
      this.loadMap();
    });
  }

  loadMap() {
    if (!this.financialData || this.financialData.length === 0) {
      return;
    }
    const chartData2: any[] = [];
    const chartVAxes: any = {};
    const chartSeries: any = {};
    this.chartAxisTimeValues = [];

    chartVAxes['0'] = {
      title: 'Coût (€)',
      minValue: 0
    };
    let i = 0;
    if (this.selectedFinancialTypes.includes('GLOBAL')) {
      chartSeries[i] = {
        targetAxisIndex: 0,
        type: 'bars',
        color: this.trackingHelper.getFinancialReportLineColor('GLOBAL'),
        labelInLegend : 'Coût ' + this.trackingHelper.getFinancialTypeName('GLOBAL')
      };
      i++;
    }
    this.selectedFinancialTypes
      .filter((type) => type !== 'GLOBAL')
      .forEach((type) => {
        chartSeries[i] = {
          targetAxisIndex: 0,
          type: 'line',
          color: this.trackingHelper.getFinancialReportLineColor(type),
          labelInLegend : 'Coût ' + this.trackingHelper.getFinancialTypeName(type)
        };
        i++;
      });

    this.financialData.forEach((data) => {
      const dataRow: any[] = [data.year];
      if (this.selectedFinancialTypes.includes('GLOBAL')) {
        dataRow.push(data.globalCost);
      }
      if (this.selectedFinancialTypes.includes('ENERGY')) {
        dataRow.push(data.energyCost);
      }
      if (this.selectedFinancialTypes.includes('MAINTENANCE')) {
        dataRow.push(data.maintenanceCost);
      }
      if (this.selectedFinancialTypes.includes('WORK')) {
        dataRow.push(data.workCost);
      }
      if (this.selectedFinancialTypes.includes('CHARGE')) {
        dataRow.push(data.chargeCost);
      }
      chartData2.push(dataRow);
      this.chartAxisTimeValues.push(data.year);
    });

    this.options.vAxes = chartVAxes;
    this.options.series = chartSeries;
    this.options = {...this.options};
    this.chartData = chartData2;
  }

  financialTypesChanged(event: any) {
    if (this.financialTypeSliders.filter((slider) => slider.checked).length === 0) {
      this.alertService.warning('Au moins un type doit être sélectionné');
      event.source.checked = true;
    } else {
      const tmpFinancialTypes = [];
      this.financialTypeSliders.forEach((slider) => {
        if (slider.checked) {
          tmpFinancialTypes.push(slider.id);
        }
      });
      this.selectedFinancialTypes = tmpFinancialTypes;
    }
    this.loadMap();
  }

  public sortDataPerYear(a: FinancialData, b: FinancialData): number {
    const orderA = parseInt(a.year, 10);
    const orderB = parseInt(b.year, 10);
    if (orderA > orderB) {
      return 1;
    }
    if (orderA < orderB) {
      return -1;
    }
    return 0;
  }

  public sortFinancialType(a: FinancialType, b: FinancialType): number {
    const orderA = Object.keys(FinancialType).indexOf(a);
    const orderB = Object.keys(FinancialType).indexOf(b);
    if (orderA > orderB) {
      return 1;
    }
    if (orderA < orderB) {
      return -1;
    }
    return 0;
  }
}
